import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, MapPin, Plus, Trash2, X } from 'lucide-react';
import {
  SERVICE_LOCALISATION_ENTITY_TYPES,
  SERVICE_LOCALISATION_TYPES_OCCUPATION,
  type ServiceLocalisationEntityType,
  type ServiceLocalisationTypeOccupation,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Panel localisations d'un service (US-SVC-09) : rattachement
 * bâtiment / étage / salle avec type d'occupation et historique.
 */

interface LocalisationRow {
  id: string;
  service_id: string;
  entity_type: ServiceLocalisationEntityType;
  entity_id: string;
  type_occupation: ServiceLocalisationTypeOccupation;
  date_debut: string;
  date_fin: string | null;
  label: string | null;
}

interface BatimentOption {
  id: string;
  code: string;
  nom: string;
}
interface EtageOption {
  id: string;
  code: string;
  numero: string;
  nom: string | null;
}
interface SalleOption {
  id: string;
  code: string;
  nom: string;
}

const ENTITY_LABELS: Record<ServiceLocalisationEntityType, string> = {
  batiment: 'Bâtiment',
  etage: 'Étage',
  salle: 'Salle',
};

const OCCUPATION_LABELS: Record<ServiceLocalisationTypeOccupation, string> = {
  principal: 'Principal',
  secondaire: 'Secondaire',
  partage: 'Partagé',
};

export function LocalisationsPanel({ serviceId }: { serviceId: string }) {
  const { can } = usePermissions();
  const canEdit = can('services', 'update');

  const [items, setItems] = useState<LocalisationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.get<{ data: LocalisationRow[] }>(
        `/services/${serviceId}/localisations`,
      );
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  async function handleDelete(id: string) {
    if (!confirm('Supprimer cette localisation ?')) return;
    try {
      await api.delete(`/services/localisations/${id}`);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {canEdit && (
        <div className="flex justify-end">
          <button
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-1" />
            Ajouter une localisation
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Aucune localisation déclarée.</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((loc) => (
            <li
              key={loc.id}
              className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2 bg-white"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 flex items-center">
                  <MapPin className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                  <span className="inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border bg-blue-100 text-blue-800 border-blue-200 mr-2">
                    {ENTITY_LABELS[loc.entity_type]}
                  </span>
                  {loc.label ?? `(${loc.entity_id.slice(0, 8)}…)`}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {OCCUPATION_LABELS[loc.type_occupation]} ·{' '}
                  {new Date(loc.date_debut).toLocaleDateString()} →{' '}
                  {loc.date_fin ? new Date(loc.date_fin).toLocaleDateString() : '—'}
                </p>
              </div>
              {canEdit && (
                <button
                  onClick={() => handleDelete(loc.id)}
                  className="p-1.5 rounded text-red-600 hover:bg-red-50"
                  aria-label="Supprimer"
                  title="Supprimer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {addOpen && (
        <AddLocalisationModal
          serviceId={serviceId}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function AddLocalisationModal({
  serviceId,
  onClose,
  onSaved,
}: {
  serviceId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [entityType, setEntityType] = useState<ServiceLocalisationEntityType>('batiment');
  const [batiments, setBatiments] = useState<BatimentOption[]>([]);
  const [etages, setEtages] = useState<EtageOption[]>([]);
  const [salles, setSalles] = useState<SalleOption[]>([]);

  const [batimentId, setBatimentId] = useState('');
  const [etageId, setEtageId] = useState('');
  const [salleId, setSalleId] = useState('');

  const [typeOccupation, setTypeOccupation] =
    useState<ServiceLocalisationTypeOccupation>('principal');
  const [dateDebut, setDateDebut] = useState(new Date().toISOString().slice(0, 10));
  const [dateFin, setDateFin] = useState('');

  // Charge les bâtiments à l'ouverture.
  useEffect(() => {
    api
      .get<{ data: BatimentOption[] }>('/batiments?page_size=200')
      .then((res) => setBatiments(res.data.data))
      .catch(() => {
        // Silencieux.
      });
  }, []);

  // Cascade étages quand un bâtiment est sélectionné.
  useEffect(() => {
    if (entityType === 'batiment') return;
    if (!batimentId) {
      setEtages([]);
      setEtageId('');
      return;
    }
    api
      .get<{ data: EtageOption[] }>(`/etages?batiment_id=${batimentId}`)
      .then((res) => setEtages(res.data.data))
      .catch(() => setEtages([]));
  }, [batimentId, entityType]);

  // Cascade salles quand un étage est sélectionné.
  useEffect(() => {
    if (entityType !== 'salle') return;
    if (!etageId) {
      setSalles([]);
      setSalleId('');
      return;
    }
    api
      .get<{ data: SalleOption[] }>(`/salles?etage_id=${etageId}&page_size=200`)
      .then((res) => setSalles(res.data.data))
      .catch(() => setSalles([]));
  }, [etageId, entityType]);

  function entityId(): string {
    if (entityType === 'batiment') return batimentId;
    if (entityType === 'etage') return etageId;
    return salleId;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const id = entityId();
    if (!id) {
      setErrorMessage('Sélectionnez une entité.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/services/${serviceId}/localisations`, {
        entity_type: entityType,
        entity_id: id,
        type_occupation: typeOccupation,
        date_debut: dateDebut,
        date_fin: dateFin || null,
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">Ajouter une localisation</h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type d'entité *
              </label>
              <select
                value={entityType}
                onChange={(e) => {
                  setEntityType(e.target.value as ServiceLocalisationEntityType);
                  setBatimentId('');
                  setEtageId('');
                  setSalleId('');
                }}
                className={inputClass}
              >
                {SERVICE_LOCALISATION_ENTITY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {ENTITY_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bâtiment *</label>
              <select
                value={batimentId}
                onChange={(e) => setBatimentId(e.target.value)}
                className={inputClass}
                required
              >
                <option value="">— Sélectionnez —</option>
                {batiments.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.code} — {b.nom}
                  </option>
                ))}
              </select>
            </div>

            {(entityType === 'etage' || entityType === 'salle') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Étage *</label>
                <select
                  value={etageId}
                  onChange={(e) => setEtageId(e.target.value)}
                  className={inputClass}
                  disabled={!batimentId}
                  required
                >
                  <option value="">— Sélectionnez —</option>
                  {etages.map((e) => (
                    <option key={e.id} value={e.id}>
                      Étage {e.numero}
                      {e.nom ? ` — ${e.nom}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {entityType === 'salle' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salle *</label>
                <select
                  value={salleId}
                  onChange={(e) => setSalleId(e.target.value)}
                  className={inputClass}
                  disabled={!etageId}
                  required
                >
                  <option value="">— Sélectionnez —</option>
                  {salles.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.code} — {s.nom}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type d'occupation *
              </label>
              <select
                value={typeOccupation}
                onChange={(e) =>
                  setTypeOccupation(e.target.value as ServiceLocalisationTypeOccupation)
                }
                className={inputClass}
              >
                {SERVICE_LOCALISATION_TYPES_OCCUPATION.map((t) => (
                  <option key={t} value={t}>
                    {OCCUPATION_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date début *</label>
                <input
                  type="date"
                  value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
                <input
                  type="date"
                  value={dateFin}
                  onChange={(e) => setDateFin(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          </form>

          <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Ajouter
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
